import {EventEmitter, Inject, Injectable} from '@angular/core';
import {from, Observable} from 'rxjs';
import {fromBER} from 'asn1js';
import {KEYUTIL, hextob64} from 'jsrsasign';
import {HttpClient} from '@angular/common/http';
import * as forge from 'node-forge';
import * as pvutils from 'pvutils';
import {LocalDateTimeService} from '@evomatik/evk-common';
import {OcspConfig} from "../componentes/config/ocsp-config";
import {Certificate, OCSPRequest, OCSPResponse} from "pkijs";
import {
    _arrayBufferToBase64,
    rootCA
} from "../componentes/firma-electronica/evk-firma-electronica/utilities/evk-firma-electronica-util";


@Injectable({
    providedIn: 'root'
})
export class FirmaElectronicaService {

    constructor(public http: HttpClient, @Inject('ocspConfig') private ocspConfig: OcspConfig, public localDateTimeService: LocalDateTimeService) {
        this.host = ocspConfig.url;
        this.ocspEnable = ocspConfig.enable
    }

    private ocspEnable: boolean;
    private host = '';
    private rfc: string;
    private password: string;
    private certificado: any;
    private llaveprivada: any;
    private hash: string;
    private signedMessageEncode: string;
    private cadenaOriginal: string;
    validacion = new ResultadoValidacionCertificadoFirmaElectronica();
    loading: EventEmitter<boolean> = new EventEmitter();
    private today = new Date();

    /**
     * Paso 1: Validación de vigencia del certificado
     */
    private paso1 = new Observable<ResultadoValidacionCertificadoFirmaElectronica>((observer) => {
        //let validacion = new ResultadoValidacionCertificadoFirmaElectronica();

        this.loading.emit(true);
        const asn1 = this.getAsn1Object(this.certificado);
        const certificado = new Certificate({schema: asn1.result});

        /** Obtenemos la fecha de hoy del sistema*/
        this.localDateTimeService.getLocalDateTimeOption().subscribe(response => {
            this.today = new Date(response.value);
            if (certificado.notBefore.value > this.today) {
                throw new Error('El certificado aun no se puede usar.');
            }
            if (certificado.notAfter.value < this.today) {
                throw new Error('El certificado a expirado.');
            }

            this.validacion.isValido = true;
            this.validacion.mensaje = 'La vigencia del certificado es valida.';

            observer.next(this.validacion);
            observer.complete();
        });


    });


    /**
     * Paso 2: Validación del password de la llave privada
     */
    private paso2 = new Observable<ResultadoValidacionCertificadoFirmaElectronica>((observer) => {
        //let validacion = new ResultadoValidacionCertificadoFirmaElectronica();

        this.validacion.isValido = true;
        this.validacion.mensaje = 'El password corresponde a la llave privada';
        this.checkPrivateKey(this.password, this.llaveprivada);
        observer.next(this.validacion);
        observer.complete();
    });


    /**
     * Paso 3: Validación del RFC del certificado
     */
    private paso3 = new Observable<ResultadoValidacionCertificadoFirmaElectronica>((observer) => {

        this.validacion.rfc = this.rfc;
        this.validacion.isValido = true;
        this.validacion.mensaje = 'El RFC del certificado corresponde al proporcionado.';
        this.validaRfcFromPem({pem: this.getPEMCertificate(this.certificado), rfc: this.rfc});
        observer.next(this.validacion);
        observer.complete();
    });

    /**
     * Paso 4: Extraer el número serial del certificado.
     */
    private paso4 = new Observable<ResultadoValidacionCertificadoFirmaElectronica>((observer) => {
        //let validacion = new ResultadoValidacionCertificadoFirmaElectronica();
        const asn1 = this.getAsn1Object(this.certificado);
        const certificado = new Certificate({schema: asn1.result});
        let serialNumber = this.getSerialNumber(certificado);

        this.validacion.serial = serialNumber;
        this.validacion.isValido = true;
        this.validacion.mensaje = 'Se obtuvo el serial del certificado.';

        observer.next(this.validacion);
        observer.complete();
    });


    /**
     * Paso 5: Validar OCSP
     */
    private paso5 = new Observable<ResultadoValidacionCertificadoFirmaElectronica>((observer) => {


        this.validacion.mensaje = 'Validación con OCSP correcta, certificado valido.';
        const asn1 = this.getAsn1Object(this.certificado);
        const certificado = new Certificate({schema: asn1.result});

        if (this.ocspEnable) {
            //OCSP Enable
            this.validarOCSP(certificado)
                .then(ocspValidacion => {

                    if (!ocspValidacion) {
                        //Si la validación de OCSP enviar false manejamos el error
                        this.validacion.isValido = false;
                        this.validacion.mensaje = 'El certificado no es válido';
                        observer.error(this.validacion.mensaje);
                    }

                    this.validacion.isValido = ocspValidacion;
                    observer.next(this.validacion);
                    observer.complete();
                })
                .catch(error => {

                    this.validacion.isValido = false;
                    this.validacion.mensaje = error;
                    observer.error(error);
                });
        } else {
            // OCSP disable
            this.validacion.isValido = true;
            observer.next(this.validacion);
            observer.complete();
        }


    });


    /**
     * Paso 6: Realizar Hash de la cadena original (Firma)
     */
    private paso6 = new Observable<ResultadoValidacionCertificadoFirmaElectronica>((observer) => {


        this.localDateTimeService.getLocalDateTimeOption().subscribe(response => {


            let signedMessage;
            /** Validamos correspondencia entre llave privada y certificado (public) */
            try {
                signedMessage = this.generarFirmarHash(this.password, this.llaveprivada, this.certificado);
            } catch (error) {
                this.validacion.isValido = false;
                this.validacion.mensaje = error;
                observer.error(error);
            }

            this.validacion.isValido = true;
            this.validacion.mensaje = 'Firmado de mensaje exitoso.';
            this.validacion.firma = signedMessage;
            this.validacion.cadenaOriginal = this.cadenaOriginal;
            // Obtenemos la cadena (label) de la fecha y asignamos como string para evitar
            // el manejo de objetos Date del JSON.stringify en el post.
            this.validacion.fechaHoraCertificado = response.label

            this.loading.emit(false);
            observer.next(this.validacion);
            observer.complete();
        });


    });


    /**
     * Realiza el proceso de validación del certificado digital de acuerdo a los pasos establecidos por el SAT.
     * @param certificado
     * @param key
     * @param password
     * @param hash
     * @param rfc
     */
    public validarCertificado = (certificado: any, key: any, password: string, hash: string, signedMessageEncode: string, rfc: string, cadenaOriginal: string) => {

        this.certificado = certificado;
        this.llaveprivada = key;
        this.password = password;
        this.rfc = rfc;
        this.hash = hash;
        this.cadenaOriginal = cadenaOriginal;
        this.signedMessageEncode = signedMessageEncode;
        this.validacion = new ResultadoValidacionCertificadoFirmaElectronica();

        return from([
            this.paso1,
            this.paso2,
            this.paso3,
            this.paso4,
            this.paso5,
            this.paso6]);


    }


    /**
     *  Petición al OCSP para validar el certificado
     */
    private validarOCSP = (certificado: Certificate): Promise<boolean> => {
        let ocspValidate = false;
        let certificateBuffer = new ArrayBuffer(0);
        certificateBuffer = this.convertPemToBinary(rootCA);


        const asn1_issuer = fromBER(certificateBuffer);
        const certIssuer = new Certificate({schema: asn1_issuer.result});
        const ocspRequest = new OCSPRequest();


        const promise = new Promise<boolean>((resolve, reject) => {
            ocspRequest.createForCertificate(certificado, {hashAlgorithm: "SHA-1", issuerCertificate: certIssuer}).then(
                () => {
                    const ocspRequestData = ocspRequest.toSchema(true).toBER(false);


                    let status = "";
                    this.verificarCertificado(ocspRequestData).subscribe(
                        response => {

                            const asn1_ocspResponse = fromBER(response);
                            const ocspRespSimpl = new OCSPResponse({schema: asn1_ocspResponse.result});


                            switch (ocspRespSimpl.responseStatus.valueBlock.valueDec) {
                                case 0:
                                    status = "successful";
                                    ocspValidate = true;
                                    break;
                                case 1:
                                    status = "malformedRequest";
                                    ocspValidate = false;
                                    break;
                                case 2:
                                    status = "internalError";
                                    ocspValidate = false;
                                    break;
                                case 3:
                                    status = "tryLater";
                                    ocspValidate = false;
                                    break;
                                case 4:
                                    status = "<not used>";
                                    ocspValidate = false;
                                    break;
                                case 5:
                                    status = "sigRequired";
                                    ocspValidate = false;
                                    break;
                                case 6:
                                    status = "unauthorized";
                                    ocspValidate = false;
                                    break;
                                default:
                                    alert("Wrong OCSP response status");
                                    ocspValidate = false;
                                    return;
                            }

                            resolve(ocspValidate);
                        }, error => {
                            reject('Ocurrio un error en la validación OCSP: ' + error);
                            //throw new Error('Ocurrio un error en la validación OCSP: ' + error);
                        }
                    );


                }
            );
        });
        return promise;

    }


    /** Valida que la llave privada corresponda al certificado, el mensaje es firmado con el key, al validar la firma con
     *  la llave publica del certificado indica si se uso el key correspondiente */
    private validaKeyCertificate = (key: any, cert: any, signed: string): any => {

        const csrPEM = this.getPEMCertificate(cert);
        let publickey = KEYUTIL.getKey(csrPEM);

        const v = publickey.verify(this.cadenaOriginal, signed);

        return v;

    }


    /**
     * Realiza la firma del hash del mensaje de la cadena origial
     */
    private generarFirmarHash = (password: string, llaveprivada: any, certificado: any): any => {


        const pkcs8PEM = this.getPEMKey(llaveprivada);

        let h, hSig;
        try {
            h = KEYUTIL.getKeyFromEncryptedPKCS8PEM(pkcs8PEM, password);

        } catch (error) {

            if (error.includes('malformed plain PKCS8 private key')) {

                throw new Error('La contraseña no es correcta.');
            } else {

                throw new Error('Ocurrió un error al desencriptar la llave privada.');
            }


        }

        hSig = this.doSign(h, this.hash, this.cadenaOriginal);

        const valid = this.validaKeyCertificate(h, certificado, hSig);
        if (valid != 1) {
            throw new Error('La llave privada proporcionada no corresponde al certificado digital o la contraseña es incorrecta.');
        }


        let sigMessage;
        if (this.signedMessageEncode === 'base64') {
            sigMessage = hextob64(hSig);
        } else if (this.signedMessageEncode === 'hex') {
            sigMessage = hSig;
        } else {
            throw new Error('La codificación del mensaje firmado no es válida');
        }

        return sigMessage;


    }

    /**
     *  Obtien el número serial del certificado
     */
    private getSerialNumber = (certificado: Certificate): string => {
        return pvutils.bufferToHexCodes(certificado.serialNumber.valueBlock.valueHex)
    }

    /**
     * Valida el RFC del usuario con el dato contenido en el certificado
     * @param param0
     */
    private validaRfcFromPem = ({pem, rfc}) => {

        const cer = this.pemToForgeCert({pem: pem});
        try {
            for (var i = 0; i < cer.subject.attributes.length; i++) {
                var val = cer.subject.attributes[i].value.trim();
                if (val.trim().toUpperCase() == rfc.trim().toUpperCase()) {
                    return true;
                }
            }
            throw new Error('El certificado digital proporcionado no coincide con los datos del usuario.');
        } catch (e) {
            throw e;
        }
    }


    /**
     * Valida la contraseña de la llave privada
     */
    private checkPrivateKey = (password: string, keyArrayBuffer: any): boolean => {
        let keyValida = false;
        const pkcs8PEM = this.getPEMKey(keyArrayBuffer);
        let h;
        try {
            h = KEYUTIL.getKeyFromEncryptedPKCS8PEM(pkcs8PEM, password);
        } catch (error) {
            keyValida = false;

            if (error.includes('malformed plain PKCS8 private key')) {

                throw new Error('La llave privada proporcionada no corresponde al certificado digital o la contraseña es incorrecta.');
            } else {

                throw new Error('Ocurrió un error al desencriptar la llave privada.');
            }
            throw error;
        }

        return keyValida;
    }

    /**
     *
     * @param certArrayBuffer
     */
    private getAsn1Object = (certArrayBuffer): any => {
        return fromBER(certArrayBuffer);
    }


    /**
     *
     * @param certArrayBuffer
     */
    private getPEMCertificate = (certArrayBuffer: any): any => {
        const certificate = '' +
            '-----BEGIN CERTIFICATE-----\r\n' +
            _arrayBufferToBase64(certArrayBuffer) +
            '\r\n-----END CERTIFICATE-----';
        return certificate;
    }

    /**
     *
     * @param keyArrayBuffer
     */
    private getPEMKey = (keyArrayBuffer: any): any => {
        const key = '' +
            '-----BEGIN ENCRYPTED PRIVATE KEY-----\r\n' +
            _arrayBufferToBase64(keyArrayBuffer) +
            '\r\n-----END ENCRYPTED PRIVATE KEY-----';
        return key;
    }

    private doSign = (rsa, hashAlg, signMessage) => {
        let hSig;
        try {
            hSig = rsa.sign(signMessage, hashAlg);
        } catch (error) {
            throw error;
        }

        return hSig;
    }


    /**
     * Petición al OCSP
     * @param arrayBuffer
     */
    private verificarCertificado(arrayBuffer) {
        return this.http.post(this.host, arrayBuffer,
            {
                headers: {
                    'Content-Type': 'application/ocsp-request'
                },
                responseType: "arraybuffer"
            }
        );
    }


    /**
     *
     * @param pem
     */
    private convertPemToBinary(pem) {
        var lines = pem.split('\n');
        var encoded = '';
        for (var i = 0; i < lines.length; i++) {
            if (lines[i].trim().length > 0 &&
                lines[i].indexOf('-BEGIN RSA PRIVATE KEY-') < 0 &&
                lines[i].indexOf('-BEGIN RSA PUBLIC KEY-') < 0 &&
                lines[i].indexOf('-BEGIN PUBLIC KEY-') < 0 &&
                lines[i].indexOf('-BEGIN CERTIFICATE-') < 0 &&
                lines[i].indexOf('-BEGIN PRIVATE KEY-') < 0 &&
                lines[i].indexOf('-END PRIVATE KEY-') < 0 &&
                lines[i].indexOf('-END CERTIFICATE-') < 0 &&
                lines[i].indexOf('-END PUBLIC KEY-') < 0 &&
                lines[i].indexOf('-END RSA PRIVATE KEY-') < 0 &&
                lines[i].indexOf('-END RSA PUBLIC KEY-') < 0) {
                encoded += lines[i].trim();
            }
        }
        return this.base64StringToArrayBuffer(encoded);
    }

    /**
     *
     * @param b64str
     */
    private base64StringToArrayBuffer(b64str) {
        let byteStr = atob(b64str);

        let bytes = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) {
            bytes[i] = byteStr.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     *
     * @param certificateBuffer
     */
    private printCertificate(certificateBuffer) {
        let asn1 = fromBER(certificateBuffer);
        if (asn1.offset === (-1)) {
            console.log("Can not parse binary data");
        }

        const certificate = new Certificate({schema: asn1.result});
        console.log(certificate);
        console.log('Certificate Serial Number');
        console.log(certificate.serialNumber.valueBlock.valueHex);
        console.log('Certificate Issuance');
        console.log(certificate.notBefore.value.toString());
        console.log('Certificate Expiry');
        console.log(certificate.notAfter.value.toString());
        console.log(certificate.issuer);
    }


    //convierte un certificado en formato pem a un certificado forge
    private pemToForgeCert({pem}) {
        try {
            var pki = forge.pki;
            return pki.certificateFromPem(pem);
        } catch (e) {
            throw "Error al convertir la cadena PEM a un certificado forge";
        }
    }

    /**
     *
     */
    getLoadingEmitter() {
        return this.loading;
    }


}

/**
 * Obteto del resultado de la validación
 */
export class ResultadoValidacionCertificadoFirmaElectronica {
    serial: string;
    rfc: string;
    mensaje: string;
    isValido: boolean;
    firma: string;
    cadenaOriginal: string;
    fechaHoraCertificado: string;

}
